/**
 * SoundEffects - Centralized SFX management for the game
 * Loads and manages all sound effects including player and robot sounds
 */

import { audioManager } from '../../engine/AudioManager';
import { debugSettings } from '../../utils/DebugSettings';

/**
 * Available sound effect names
 */
export enum SFX {
  // Movement sounds
  LAND_IMPACT = 'land_impact',
  JUMP = 'jump',
  FOOTSTEP = 'footstep',

  // Combat sounds
  PLAYER_HIT = 'player_hit',

  // Ambient sounds
  AMBIENT_ROOM = 'ambient_room',
  AMBIENT_VENT = 'ambient_vent',
  AMBIENT_MACHINE = 'ambient_machine',

  // UI sounds
  UI_CLICK = 'ui_click',
  UI_HOVER = 'ui_hover',
  UI_OPEN = 'ui_open',
  UI_CLOSE = 'ui_close',
  UI_ERROR = 'ui_error',

  // Jumpscare sounds
  JUMPSCARE_SCREAM = 'jumpscare_scream',
  JUMPSCARE_STATIC = 'jumpscare_static',

  // Environmental sounds
  DOOR_OPEN = 'door_open',
  DOOR_CLOSE = 'door_close',
  CAMERA_SWITCH = 'camera_switch',
  FLASHLIGHT_ON = 'flashlight_on',
  FLASHLIGHT_OFF = 'flashlight_off',
  VENT_CRAWL = 'vent_crawl',
  ROBOT_FOOTSTEP = 'robot_footstep',
  ROBOT_VOICE = 'robot_voice',

  // Collectible sounds
  COLLECTIBLE_PICKUP = 'collectible_pickup',
  TASER_PICKUP = 'taser_pickup',

  // Detection sounds
  DETECTION_BEEP = 'detection_beep',
  DETECTION_FULL = 'detection_full',
}

/**
 * Load all game sound effects
 */
export function loadAllSoundEffects(): void {
  console.log('🔊 Loading all sound effects...');

  // Load landing impact sound
  console.log('📥 Loading landing sound: /sounds/815336__artninja__superhero_earth_impact_hit_07062025.wav');
  audioManager.loadSFX(SFX.LAND_IMPACT, {
    src: ['/sounds/815336__artninja__superhero_earth_impact_hit_07062025.wav'],
    volume: 0.55,
    pool: 10,
  });

  // Load jump sound
  console.log('📥 Loading jump sound: /sounds/368831__jofae__sick-player-jump.mp3');
  audioManager.loadSFX(SFX.JUMP, {
    src: ['/sounds/368831__jofae__sick-player-jump.mp3'],
    volume: 1.0,
    pool: 5, // Allow multiple simultaneous jump sounds
  });

  // Load footstep sound - Horse footstep sound
  console.log('📥 Loading horse footstep sound: /sounds/807855__designerschoice__feethors-blue-snowball-microphone-cu_footsteps-horse-wood-simulated_nicholas-judy_tdc.wav');
  audioManager.loadSFX(SFX.FOOTSTEP, {
    src: ['/sounds/807855__designerschoice__feethors-blue-snowball-microphone-cu_footsteps-horse-wood-simulated_nicholas-judy_tdc.wav'],
    volume: 1.0,
    loop: true,  // Enable looping for footsteps
    pool: 10,
    rate: 1.0,
  });
  console.log('✅ Horse footstep sound loading...');

  // Load collectible pickup sound
  console.log('📥 Loading collectible pickup sound: /sounds/320902__suzenako__ding3.wav');
  audioManager.loadSFX(SFX.COLLECTIBLE_PICKUP, {
    src: ['/sounds/320902__suzenako__ding3.wav'],
    volume: 1.0,
    pool: 10, // Allow multiple simultaneous pickup sounds
  });
  console.log('✅ Collectible pickup sound loading...');

  // Load taser pickup sound
  audioManager.loadSFX(SFX.TASER_PICKUP, {
    src: ['/sounds/taser.wav'],
    volume: 0.8,
    pool: 3,
  });

  // Load player hit sound (when robot hits player)
  console.log('📥 Loading player hit sound: /sounds/573376__johnloser__cyber-punch-01.wav');
  audioManager.loadSFX(SFX.PLAYER_HIT, {
    src: ['/sounds/573376__johnloser__cyber-punch-01.wav'],
    volume: 0.85,
    pool: 5,
  });
  console.log('✅ Player hit sound loading...');

  // Load UI sounds - use placeholder silence or existing sounds for now
  // These need to be loaded even if files don't exist to prevent errors
  console.log('📥 Loading UI sounds...');

  // Use the click sound for UI click
  audioManager.loadSFX(SFX.UI_CLICK, {
    src: ['/sounds/423774__someonecool15__click-5.mp3'],
    volume: 0.5,
    pool: 5,
  });

  // UI hover - NO SOUND (placeholder with volume 0)
  audioManager.loadSFX(SFX.UI_HOVER, {
    src: ['/sounds/423774__someonecool15__click-5.mp3'],
    volume: 0.0, // Muted - no hover sound
    pool: 5,
  });

  // Use collectible sound for UI open/close
  audioManager.loadSFX(SFX.UI_OPEN, {
    src: ['/sounds/320902__suzenako__ding3.wav'],
    volume: 0.4,
    pool: 5,
  });

  audioManager.loadSFX(SFX.UI_CLOSE, {
    src: ['/sounds/320902__suzenako__ding3.wav'],
    volume: 0.3,
    pool: 5,
  });

  // Use landing sound for UI error
  audioManager.loadSFX(SFX.UI_ERROR, {
    src: ['/sounds/815336__artninja__superhero_earth_impact_hit_07062025.wav'],
    volume: 0.2,
    pool: 5,
  });

  console.log('✅ UI sounds loaded (using placeholder audio files)');
  // ── Ambient sound layers ─────────────────────────────────────────────
  // Files used: existing assets repurposed at very low volume for atmosphere.
  // Replace with dedicated ambient WAV/MP3 files when available.
  audioManager.loadSFX(SFX.AMBIENT_ROOM, {
    // Soft low hum — building ventilation / electrical (floor 1 public area)
    src: ['/sounds/815336__artninja__superhero_earth_impact_hit_07062025.wav'],
    volume: 0.04,
    loop: true,
    pool: 2,
    rate: 0.25, // Very slow playback = deep rumble
  });

  audioManager.loadSFX(SFX.AMBIENT_MACHINE, {
    // Mechanical thud loop — workshop / restricted floor 3
    src: ['/sounds/807855__designerschoice__feethors-blue-snowball-microphone-cu_footsteps-horse-wood-simulated_nicholas-judy_tdc.wav'],
    volume: 0.06,
    loop: true,
    pool: 2,
    rate: 0.4,
  });

  audioManager.loadSFX(SFX.AMBIENT_VENT, {
    // Airy whisper — near vents, floor 2 corridors
    src: ['/sounds/807855__designerschoice__feethors-blue-snowball-microphone-cu_footsteps-horse-wood-simulated_nicholas-judy_tdc.wav'],
    volume: 0.035,
    loop: true,
    pool: 2,
    rate: 0.6,
  });

  // Detection beep — plays as robot suspicion bar fills, pitch rises with suspicion level
  audioManager.loadSFX(SFX.DETECTION_BEEP, {
    src: ['/sounds/654411__krokulator__select2.wav'],
    volume: 0.4,
    pool: 6,
  });

  // Detection full — plays once when suspicion bar reaches 100% (robot locks on)
  audioManager.loadSFX(SFX.DETECTION_FULL, {
    src: ['/sounds/33788__jobro__5-beep-b.wav'],
    volume: 0.8,
    pool: 2,
  });

  console.log('✅ Sound effects loading initiated - check for load success messages from AudioManager');
}

/**
 * Play a landing sound based on fall velocity
 * @param velocity - Vertical velocity when landing (used to adjust volume)
 */
export function playLandingSound(velocity: number, volume: number = 1.0): void {
  console.log(`🔊 playLandingSound() CALLED! Velocity: ${velocity}`);

  // ALWAYS play for testing - remove threshold
  console.log(`🔊 PLAYING LANDING SOUND NOW!`);

  // Play and apply distance-based volume if provided
  const soundId = audioManager.playSFX(SFX.LAND_IMPACT);
  if (soundId !== null && volume < 1.0) { // ← distance volume
    const sfx = (audioManager as any).sfxPool?.get(SFX.LAND_IMPACT);
    if (sfx) sfx.volume(Math.max(0, Math.min(1, volume)), soundId);
  }
  console.log(`🔊 Landing sound ID: ${soundId}`);
}

/**
 * Play jump sound - ALWAYS plays when called
 */
export function playJumpSound(volume: number = 1.0): void {
  if (debugSettings.enableAudioLogging) {
    console.log('🚀 playJumpSound called! PLAYING EVERY TIME!');
  }
  const soundId = audioManager.playSFX(SFX.JUMP);
  if (soundId !== null && volume < 1.0) { // ← distance volume
    const sfx = (audioManager as any).sfxPool?.get(SFX.JUMP);
    if (sfx) sfx.volume(Math.max(0, Math.min(1, volume)), soundId);
  }
  if (debugSettings.enableAudioLogging) {
    console.log(`🎵 Jump sound ID: ${soundId}`);
    if (soundId !== null) {
      console.log(`✅ Jump sound played successfully!`);
    } else {
      console.error('❌ Failed to play jump sound - soundId is null');
    }
  }
}

/**
 * Play a specific sound effect by name
 */
export function playSFX(sfxName: SFX): void {
  audioManager.playSFX(sfxName);
}

/**
 * Play footstep sound (looping) - returns sound ID for stopping later
 */
export function playFootstepSound(): number | null {
  const soundId = audioManager.playSFX(SFX.FOOTSTEP);

  if (soundId !== null) {
    const sfx = (audioManager as any).sfxPool.get(SFX.FOOTSTEP);
    if (sfx) {
      sfx.loop(true, soundId);
      sfx.volume(1.0, soundId);
    }
  }

  return soundId;
}

/**
 * Stop footstep sound by ID
 */
export function stopFootstepSound(soundId: number | null): void {
  if (soundId === null) {
    return;
  }

  const sfx = (audioManager as any).sfxPool.get(SFX.FOOTSTEP);
  if (sfx) {
    sfx.stop(soundId);
  }
}

// ============================================
// AMBIENT SOUNDS
// ============================================

/**
 * Play ambient room sound (looping)
 */
export function playAmbientRoom(): number | null {
  return audioManager.playSFX(SFX.AMBIENT_ROOM);
}

/**
 * Play ambient vent sound (looping)
 */
export function playAmbientVent(): number | null {
  return audioManager.playSFX(SFX.AMBIENT_VENT);
}

/**
 * Play ambient machine sound (looping)
 */
export function playAmbientMachine(): number | null {
  return audioManager.playSFX(SFX.AMBIENT_MACHINE);
}

// ============================================
// UI SOUNDS
// ============================================

/**
 * Play UI click sound
 */
export function playUIClick(): void {
  audioManager.playSFX(SFX.UI_CLICK);
}

/**
 * Play UI hover sound
 */
export function playUIHover(): void {
  audioManager.playSFX(SFX.UI_HOVER);
}

/**
 * Play UI open sound
 */
export function playUIOpen(): void {
  audioManager.playSFX(SFX.UI_OPEN);
}

/**
 * Play UI close sound
 */
export function playUIClose(): void {
  audioManager.playSFX(SFX.UI_CLOSE);
}

/**
 * Play UI error sound
 */
export function playUIError(): void {
  audioManager.playSFX(SFX.UI_ERROR);
}

// ============================================
// JUMPSCARE SOUNDS
// ============================================

/**
 * Play jumpscare scream
 */
export function playJumpscareScream(): void {
  audioManager.playSFX(SFX.JUMPSCARE_SCREAM);
}

/**
 * Play jumpscare static
 */
export function playJumpscareStatic(): void {
  audioManager.playSFX(SFX.JUMPSCARE_STATIC);
}

// ============================================
// ENVIRONMENTAL SOUNDS
// ============================================

/**
 * Play door open sound
 */
export function playDoorOpen(): void {
  audioManager.playSFX(SFX.DOOR_OPEN);
}

/**
 * Play door close sound
 */
export function playDoorClose(): void {
  audioManager.playSFX(SFX.DOOR_CLOSE);
}

/**
 * Play camera switch sound
 */
export function playCameraSwitch(): void {
  audioManager.playSFX(SFX.CAMERA_SWITCH);
}

/**
 * Play flashlight on sound
 */
export function playFlashlightOn(): void {
  audioManager.playSFX(SFX.FLASHLIGHT_ON);
}

/**
 * Play flashlight off sound
 */
export function playFlashlightOff(): void {
  audioManager.playSFX(SFX.FLASHLIGHT_OFF);
}

/**
 * Play vent crawl sound
 */
export function playVentCrawl(): void {
  audioManager.playSFX(SFX.VENT_CRAWL);
}

/**
 * Play robot footstep sound
 */
export function playRobotFootstep(): void {
  audioManager.playSFX(SFX.ROBOT_FOOTSTEP);
}

/**
 * Play robot voice sound
 */
export function playRobotVoice(): void {
  audioManager.playSFX(SFX.ROBOT_VOICE);
}

// ============================================
// COLLECTIBLE SOUNDS
// ============================================

/**
 * Play collectible pickup sound
 */
export function playCollectiblePickup(): void {
  audioManager.playSFX(SFX.COLLECTIBLE_PICKUP);
}

export function playTaserPickup(): void {
  audioManager.playSFX(SFX.TASER_PICKUP);
}

// ============================================
// DETECTION SOUNDS
// ============================================

/**
 * Play the robot detection beep at a given pitch and volume.
 * Pitch increases as suspicion rises; volume fades with distance.
 */
export function playDetectionFull(volume: number = 1.0): void {
  const sfx = (audioManager as any).sfxPool?.get(SFX.DETECTION_FULL);
  if (!sfx) return;
  const soundId = sfx.play();
  if (soundId !== null && volume < 1.0) { // ← distance volume
    sfx.volume(Math.max(0, Math.min(1, volume)), soundId);
  }
}

export function playDetectionBeep(pitch: number, volume: number = 0.4): void {
  const sfx = (audioManager as any).sfxPool?.get(SFX.DETECTION_BEEP);
  if (!sfx) return;
  const soundId = sfx.play();
  if (soundId !== null) {
    sfx.rate(Math.max(0.5, Math.min(3.0, pitch)), soundId);
    sfx.volume(Math.max(0, Math.min(1, volume)), soundId);
  }
}

// ============================================
// COMBAT SOUNDS
// ============================================

/**
 * Play player hit sound (when robot hits player) and duck music briefly
 */
export function playPlayerHitSound(): void {
  audioManager.playSFX(SFX.PLAYER_HIT);
  audioManager.duckMusic(0.2, 900, 150); // drop to 20% for 900ms then restore
}
